import classNames from 'classnames';
import { FunctionComponent, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { MessageBundle } from '@amzn/arb-tools';
import {
  Icon,
  SelectGroup,
  AsxMarkdownContent,
  PrimaryButton,
  PRIMARY_BUTTON_SIZE,
  useNavbarPositioning,
  ICON_NAME,
  PRIMARY_BUTTON_ICON_POSITION,
  PRIMARY_BUTTON_VARIANT,
} from '@amzn/sss-website-theme';

import { isWeblabSegmentPagesEnabled } from 'src/clients/weblab/weblab-client';
import {
  INTAKE_FORM_KEYS,
  OrganizationCommitmentsFormValues,
  monthYearRegex,
  QUESTION_RESPONSE,
  validateMonthYearDateInput,
  validateQuestionResponse,
  yearRegex,
  VALID_YES_NO_RESPONSES,
  VALID_YES_NO_IN_PROCESS_RESPONSES,
} from 'src/components/pages/intake-form/FormEnums';
import { IntakeFormQuestionInfo, FormStrings, TARGET_DATE_VALIDATION_ERROR_LABEL } from 'src/constants';
import { ROUTES } from 'src/enums';
import { addRequiredLabelSymbol, t } from 'src/helpers';

import styles from './OrganizationCommitmentsForm.module.scss';

export interface OrganizationCommitmentsFormProp {
  intakeFormBundle: MessageBundle;
  handleFormSubmit: (data: OrganizationCommitmentsFormValues) => void;
  backCtaLabel: string;
  ctaLabel: string;
  defaultValues?: Partial<OrganizationCommitmentsFormValues>;
  isRebrandingActive?: boolean;
}

export const OrganizationCommitmentsForm: FunctionComponent<OrganizationCommitmentsFormProp> = ({
  intakeFormBundle,
  handleFormSubmit,
  backCtaLabel,
  ctaLabel,
  defaultValues = {},
  isRebrandingActive,
}) => {
  const {
    register,
    unregister,
    handleSubmit,
    getValues,
    setValue,
    resetField,
    clearErrors,
    control,
    watch,
    formState: { errors },
  } = useForm<OrganizationCommitmentsFormValues>({
    reValidateMode: 'onSubmit',
    defaultValues,
  });
  const errorsPresentRef = useRef<HTMLDivElement>(null);
  const formHasErrors = Object.keys(errors).length > 0;

  const { mainNavbarHeight } = useNavbarPositioning();

  const commitmentsForm = 'commitmentsForm';

  const yesNoOptions = [
    { value: QUESTION_RESPONSE.YES, optionLabel: t(intakeFormBundle, FormStrings.YES_SELECTOR_OPTION) },
    { value: QUESTION_RESPONSE.NO, optionLabel: t(intakeFormBundle, FormStrings.NO_SELECTOR_OPTION) },
  ];

  const yesNoInProcessOptions = [
    { value: QUESTION_RESPONSE.YES, optionLabel: t(intakeFormBundle, FormStrings.YES_SELECTOR_OPTION) },
    { value: QUESTION_RESPONSE.NO, optionLabel: t(intakeFormBundle, FormStrings.NO_SELECTOR_OPTION) },
    { value: QUESTION_RESPONSE.IN_PROCESS, optionLabel: t(intakeFormBundle, FormStrings.IN_PROCESS_SELECTOR_OPTION) },
  ];

  useEffect(() => {
    if (formHasErrors && errorsPresentRef.current) {
      // We want to offset our scroll by our navbar height to avoid our errors present element being
      // hidden behind the navbar
      const navbarOffset = mainNavbarHeight;
      const topOffset = errorsPresentRef.current.getBoundingClientRect().top + window.scrollY - navbarOffset;
      window.scrollTo({ behavior: 'smooth', top: topOffset });
    }
  }, [formHasErrors, mainNavbarHeight]);

  const watchQuestionOneResponse = watch(INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION)?.includes(
    QUESTION_RESPONSE.YES
  );
  const watchQuestionThreeResponse = watch(INTAKE_FORM_KEYS.DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION)?.includes(
    QUESTION_RESPONSE.IN_PROCESS
  );

  useEffect(() => {
    // Clear errors when the component mounts
    clearErrors();

    // Unregister additional fields when the 'other' option is deselected
    if (watchQuestionOneResponse) {
      register(INTAKE_FORM_KEYS.SUBQUESTION_1);
    } else if (!watchQuestionOneResponse) {
      // Reset field to clear value and errors before unregistering
      resetField(INTAKE_FORM_KEYS.SUBQUESTION_1);
      unregister(INTAKE_FORM_KEYS.SUBQUESTION_1);
    }

    if (watchQuestionThreeResponse) {
      register(INTAKE_FORM_KEYS.SUBQUESTION_3);
    } else if (!watchQuestionThreeResponse) {
      // Reset field to clear value and errors before unregistering
      resetField(INTAKE_FORM_KEYS.SUBQUESTION_3);
      unregister(INTAKE_FORM_KEYS.SUBQUESTION_3);
    }
  }, [clearErrors, watchQuestionOneResponse, watchQuestionThreeResponse, register, unregister, resetField]);

  return (
    <div>
      {formHasErrors && (
        <div className={styles.formErrorsPresentWrapper} ref={errorsPresentRef}>
          <Icon name="error" className={styles.formErrorsPresentIcon} />
          <p className={styles.formErrorsPresent}>{t(intakeFormBundle, FormStrings.ERRORS_PRESENT_DESCRIPTION)}</p>
        </div>
      )}
      <div>
        <p className={styles.formRequiredFields}>{t(intakeFormBundle, FormStrings.REQUIRED_FIELDS_LABEL)}</p>
      </div>
      <div>
        <h4 className={styles.formSectionHeader}>{t(intakeFormBundle, FormStrings.COMMITMENTS_INFORMATION_HEADER)}</h4>
      </div>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate id={commitmentsForm}>
        <div className={styles.formInputWrapperLarge}>
          <Controller
            name={INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION}
            control={control}
            rules={{
              required: {
                value: true,
                message: t(intakeFormBundle, FormStrings.QUESTION_1_ERROR),
              },
              validate: (value) =>
                validateQuestionResponse(
                  value,
                  VALID_YES_NO_RESPONSES,
                  t(intakeFormBundle, FormStrings.QUESTION_1_ERROR)
                ),
            }}
            render={({ field: { onChange } }) => (
              <SelectGroup
                isRebrandingActive={isRebrandingActive}
                selectionType="single"
                id={INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION}
                label={addRequiredLabelSymbol(
                  t(intakeFormBundle, IntakeFormQuestionInfo.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION.text)
                )}
                options={yesNoOptions}
                testId={INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION}
                onChange={(selectedValue, isChecked) => {
                  if (isChecked) {
                    onChange(selectedValue);
                    setValue(INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION, selectedValue!);
                  } else {
                    onChange('');
                    setValue(INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION, '');
                  }
                  clearErrors(INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION);
                }}
                errors={errors[INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION]}
                showAdditionalInput={watchQuestionOneResponse}
                additionalInputProps={{
                  label: addRequiredLabelSymbol(t(intakeFormBundle, IntakeFormQuestionInfo.SUBQUESTION_1.text)),
                  id: INTAKE_FORM_KEYS.SUBQUESTION_1,
                  additionalInputValue: getValues(INTAKE_FORM_KEYS.SUBQUESTION_1) ?? '',
                  onChange: (nestedInputValue) => {
                    setValue(INTAKE_FORM_KEYS.SUBQUESTION_1, nestedInputValue);
                    clearErrors(INTAKE_FORM_KEYS.SUBQUESTION_1);
                  },
                  register,
                  registerOptions: {
                    required: t(intakeFormBundle, FormStrings.SUBQUESTION_1_ERROR),
                    pattern: {
                      value: yearRegex,
                      message: t(intakeFormBundle, FormStrings.ERROR_YEAR_DATE),
                    },
                  },
                  error: errors[INTAKE_FORM_KEYS.SUBQUESTION_1],
                  placeholder: 'YYYY',
                }}
              />
            )}
          />
        </div>
        <div className={styles.formInputWrapperLarge}>
          <Controller
            name={INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION}
            control={control}
            rules={{
              required: {
                value: true,
                message: t(intakeFormBundle, FormStrings.QUESTION_4_ERROR),
              },
              validate: (value) =>
                validateQuestionResponse(
                  value,
                  VALID_YES_NO_RESPONSES,
                  t(intakeFormBundle, FormStrings.QUESTION_4_ERROR)
                ),
            }}
            render={({ field: { onChange } }) => (
              <SelectGroup
                isRebrandingActive={isRebrandingActive}
                selectionType="single"
                id={INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION}
                label={addRequiredLabelSymbol(
                  t(intakeFormBundle, IntakeFormQuestionInfo.MEASURE_AND_REPORT_CRITERIA_QUESTION.text)
                )}
                options={yesNoOptions}
                testId={INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION}
                onChange={(selectedValue, isChecked) => {
                  if (isChecked) {
                    onChange(selectedValue);
                    setValue(INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION, selectedValue);
                  } else {
                    onChange('');
                    setValue(INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION, '');
                  }
                  clearErrors(INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION);
                }}
                errors={errors[INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION]}
              />
            )}
          />
        </div>
        {!isWeblabSegmentPagesEnabled && (
          <div className={styles.formInputWrapperLarge}>
            <Controller
              name={INTAKE_FORM_KEYS.DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION}
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t(intakeFormBundle, FormStrings.QUESTION_3_ERROR),
                },
                validate: (value) =>
                  validateQuestionResponse(
                    value,
                    VALID_YES_NO_IN_PROCESS_RESPONSES,
                    t(intakeFormBundle, FormStrings.QUESTION_3_ERROR)
                  ),
              }}
              render={({ field: { onChange } }) => (
                <SelectGroup
                  isRebrandingActive={isRebrandingActive}
                  selectionType="single"
                  id={INTAKE_FORM_KEYS.DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION}
                  label={addRequiredLabelSymbol(
                    t(intakeFormBundle, IntakeFormQuestionInfo.DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION.text)
                  )}
                  options={yesNoInProcessOptions}
                  testId={INTAKE_FORM_KEYS.DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION}
                  onChange={(selectedValue, isChecked) => {
                    if (isChecked) {
                      onChange(selectedValue);
                      setValue(INTAKE_FORM_KEYS.DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION, selectedValue);
                    } else {
                      onChange('');
                      setValue(INTAKE_FORM_KEYS.DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION, '');
                    }
                    clearErrors(INTAKE_FORM_KEYS.DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION);
                    clearErrors(INTAKE_FORM_KEYS.SUBQUESTION_3);
                  }}
                  errors={errors[INTAKE_FORM_KEYS.DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION]}
                  showAdditionalInput={watchQuestionThreeResponse}
                  additionalInputProps={{
                    label: addRequiredLabelSymbol(t(intakeFormBundle, IntakeFormQuestionInfo.SUBQUESTION_3.text)),
                    id: INTAKE_FORM_KEYS.SUBQUESTION_3,
                    additionalInputValue: getValues(INTAKE_FORM_KEYS.SUBQUESTION_3) ?? '',
                    onChange: (nestedInputValue) => {
                      setValue(INTAKE_FORM_KEYS.SUBQUESTION_3, nestedInputValue);
                      clearErrors(INTAKE_FORM_KEYS.SUBQUESTION_3);
                    },
                    register,
                    registerOptions: {
                      required: t(intakeFormBundle, FormStrings.SUBQUESTION_3_ERROR),
                      pattern: {
                        value: monthYearRegex,
                        message: t(intakeFormBundle, FormStrings.ERROR_MONTH_YEAR_DATE),
                      },
                      validate: (inputValue: string) =>
                        validateMonthYearDateInput(inputValue, t(intakeFormBundle, TARGET_DATE_VALIDATION_ERROR_LABEL)),
                    },
                    error: errors[INTAKE_FORM_KEYS.SUBQUESTION_3],
                    placeholder: 'MM/YYYY',
                  }}
                />
              )}
            />
          </div>
        )}
        <div className={styles.submitButtonWrapper} data-testid="submitButtonWrapper">
          <AsxMarkdownContent
            isRebrandingActive={isRebrandingActive}
            className={styles.privacyNoticeLabel}
            copy={t(intakeFormBundle, FormStrings.ORIGINAL_INTAKE_FORM_PRIVACY_NOTICE)}
          />

          {isWeblabSegmentPagesEnabled ? (
            <div className={styles.buttonsWrapper}>
              <PrimaryButton
                isRebrandingActive={isRebrandingActive}
                label={backCtaLabel}
                icon={ICON_NAME.CHEVRON_LEFT}
                iconPosition={PRIMARY_BUTTON_ICON_POSITION.Before}
                data-testid="back-button"
                // the styles shown on Figma doesn't apply to any variant. "Rate" variant is the closest one
                variant={isRebrandingActive ? PRIMARY_BUTTON_VARIANT.Rate : PRIMARY_BUTTON_VARIANT.Default}
                buttonSize={PRIMARY_BUTTON_SIZE.Small}
                className={styles.backButton}
                link={ROUTES.INTAKE_PAGE_STEP_1}
                forceServerSideNav
              />
              <PrimaryButton
                isRebrandingActive={isRebrandingActive}
                label={ctaLabel}
                icon={ICON_NAME.CHEVRON_RIGHT}
                type="submit"
                form={commitmentsForm}
                data-testid="submit-button"
                buttonSize={PRIMARY_BUTTON_SIZE.Small}
                className={classNames(!isRebrandingActive && styles.submitButton)}
              />
            </div>
          ) : (
            <PrimaryButton
              label={ctaLabel}
              isRebrandingActive={isRebrandingActive}
              icon={ICON_NAME.CHEVRON_RIGHT}
              type="submit"
              form={commitmentsForm}
              data-testid="submit-button"
              buttonSize={PRIMARY_BUTTON_SIZE.Small}
              className={classNames(!isRebrandingActive && styles.submitButton)}
            />
          )}
        </div>
      </form>
    </div>
  );
};
